import type { FirstReceiverSummary, SkuRow } from "./types"
import { sameRounded } from "./utils"

/** Single source of truth for the FE-facing measure-key strings used outside
 *  generic `measures[k]` lookups. Keep in lockstep with VT measure names. */
export const MEASURE = {
  STARTING_INVENTORY: "starting_inventory",
  OPEN_SHIPMENTS_RECEIVED: "open_shipments_received",
  SIMULATED_SHIPMENTS_RECEIVED: "simulated_shipments_received",
  SIMULATED_SHIPMENTS_PLACED_ATOMIC: "simulated_shipments_placed_atomic",
  SIMULATED_SHIPMENTS_PLACED_PLANNER_OVERRIDE: "simulated_shipments_placed_planner_override",
  FORECAST_SHIPMENTS_DELTA: "forecast_shipments_delta",
  ENDING_INVENTORY: "ending_inventory",
  ENDING_DAYS_OF_SUPPLY: "ending_days_of_supply",
} as const

/**
 * Compute days of supply per period — same logic as the VT _compute_days_of_supply.
 * For each period, counts how many future periods the ending inventory can cover
 * given cumulative demand.
 */
function computeDaysOfSupply(endingInv: number[], sales: number[]): number[] {
  const n = endingInv.length
  // Build cumulative demand
  const cumDemand = new Array(n).fill(0)
  for (let i = 0; i < n; i++) {
    cumDemand[i] = Math.abs(sales[i]) + (i > 0 ? cumDemand[i - 1] : 0)
  }

  return endingInv.map((inv, i) => {
    // If every remaining future period has zero forecast, DOS is unbounded —
    // surface as NaN so the renderer shows "-" instead of a placeholder count.
    const remainingDemand = cumDemand[n - 1] - cumDemand[i]
    if (remainingDemand === 0) return Number.NaN
    let count = 0
    for (let j = i + 1; j < n; j++) {
      const demandSince = cumDemand[j] - cumDemand[i]
      if (inv > demandSince) count++
      else break
    }
    return count
  })
}

/** Mask per-period WOS values where there's no remaining forecast demand,
 *  mirroring computeDaysOfSupply's NaN rule. The aggregate panel computes WOS
 *  locally via computeDaysOfSupply, so it already gets NaN-for-empty-demand;
 *  per-SKU baseline values come from the server (or recalcMeasures' countDOS)
 *  and don't carry that signal — apply this before rendering so the SKU row +
 *  WOS detail row match the aggregate's "-" treatment. */
export function maskWOSForEmptyDemand(
  wosValues: number[],
  forecastSales: number[],
): number[] {
  const n = wosValues.length
  // remaining[i] = sum(|sales[j]|) for j > i. Ending DOS at period i looks
  // forward from period i+1, matching computeDaysOfSupply's invariant.
  const remaining = new Array(n).fill(0)
  for (let i = n - 2; i >= 0; i--) {
    remaining[i] = remaining[i + 1] + Math.abs(forecastSales[i + 1] ?? 0)
  }
  return wosValues.map((v, i) => (remaining[i] === 0 ? Number.NaN : v))
}

/**
 * Recalculate the supply walk after editing simulated_shipment_orders at a given week.
 * Orders at week N arrive at week N + leadTime (from data, defaults to 2).
 *
 * Mirrors VT `_recalc_group` for the inventory-walk fields, plus any FE-derived
 * measures (e.g. `forecast_shipments_delta`). Every key cloned from `measures`
 * carries forward by reference-of-fresh-array; downstream readers (sort
 * comparator, FR aggregator, cell renderer) read these directly, so any
 * derived measure that depends on the edited cell MUST be rewritten here or
 * its overlay value will go stale.
 */
const DEFAULT_LEAD_TIME = 2

export function recalcMeasures(
  measures: Record<string, (number | null)[]>,
  weekIndex: number,
  newOrderValue: number,
  leadTime?: number,
  /**
   * CHO-459 follow-up: per-period USD/case price. When provided, recalc
   * rewrites forecast_shipment_revenue[i] = ssp_planner_override[i] × price[i]
   * so the $-row tracks planner edits live. Null/missing prices yield 0
   * (don't fabricate dollars from missing data).
   */
  unitPriceShipping?: (number | null)[],
): Record<string, number[]> {
  const effectiveLeadTime = leadTime ?? DEFAULT_LEAD_TIME
  // Clone with null → 0 coercion at the boundary. The walk math below
  // produces numbers everywhere it writes (Math.max, Math.min, integer
  // arithmetic) so the output is non-nullable by construction. Any input
  // null (CHO-610 suppressed measures on fallback rows) collapses to 0 here
  // — a known consequence: planner edits on a fallback row will surface
  // walked numbers in the aggregate that were null pre-edit. Acceptable
  // for now since the suppressed-measure rows are hidden in the expanded
  // walk anyway, but worth knowing if behavior gets debated.
  const next: Record<string, number[]> = {}
  for (const k of Object.keys(measures)) {
    next[k] = measures[k].map((v) => v ?? 0)
  }
  const n = next.starting_inventory.length
  const isActuals = next.is_actuals ?? []

  // Apply the new override
  next.simulated_shipments_placed_planner_override[weekIndex] = newOrderValue

  // Rebuild simulated_shipments_received from planner_override for FORECAST
  // weeks only. Actuals-week arrivals are the engine's historical record of
  // what actually showed up — planner overrides don't retroactively change
  // history. Mirrors the BE _recalc_supply_walk post-CHO-453 invariant: for
  // is_act[i], we trust engine values rather than walking from override.
  for (let i = 0; i < n; i++) {
    if (!isActuals[i]) next.simulated_shipments_received[i] = 0
  }
  for (let i = 0; i < n; i++) {
    const arrival = i + effectiveLeadTime
    if (arrival < n && !isActuals[arrival]) {
      next.simulated_shipments_received[arrival] = next.simulated_shipments_placed_planner_override[i]
    }
  }

  // Inventory walk: trust engine values for actuals weeks (the engine's
  // backward walk anchors on a known IOH at "today" and reconciles via
  // fallback_starting_ioh, producing negative starting_inventory values for
  // weeks before the anchor — see thoughts/transforms/engine_inputs/
  // fallback_starting_ioh.md). Walking forward from those negatives produces
  // phantom inventory by the actuals→forecast boundary (e.g. LCJ60P:
  // starting_inventory[0]=-562 walks forward to ending[11]=726 instead of
  // the engine-anchored 164). For actuals weeks, the cloned next.* already
  // holds engine values; just skip them.
  const effectiveSales = (i: number) =>
    isActuals[i] ? (next.actual_sales?.[i] ?? next.forecast_sales[i]) : next.forecast_sales[i]

  const countDOS = (inv: number, from: number) => {
    let cum = 0
    let count = 0
    for (let j = from; j < n; j++) {
      cum += Math.abs(effectiveSales(j))
      if (inv > cum) count++
      else break
    }
    return count
  }

  for (let i = 0; i < n; i++) {
    if (isActuals[i]) continue
    // For the first forecast week, starting_inventory[i] = engine's
    // ending_inventory[i-1] (the actuals-anchored value, untouched by the
    // skip above). For subsequent forecast weeks, walk from the just-
    // computed ending_inventory[i-1].
    if (i > 0) next.starting_inventory[i] = next.ending_inventory[i - 1]
    const available =
      next.starting_inventory[i] +
      next.open_shipments_received[i] +
      next.simulated_shipments_received[i]
    const grossDemand = next.forecast_sales[i]
    const fulfilled = Math.min(grossDemand, Math.max(0, available))
    next.consumed_demand[i] = fulfilled
    next.unfulfilled_demand[i] = grossDemand - fulfilled
    next.ending_inventory[i] = Math.max(0, available - fulfilled)
    next.net_supply_balance[i] =
      next.open_shipments_received[i] + next.simulated_shipments_received[i] - fulfilled
    next.starting_days_of_supply[i] = countDOS(next.starting_inventory[i], i)
    next.ending_days_of_supply[i] = countDOS(next.ending_inventory[i], i + 1)
  }

  // Mirror VT: forecast_shipments_delta = planner_override - atomic. Recomputed
  // here so the overlay carries a live value for sort/aggregation/render.
  if (next.simulated_shipments_placed_atomic) {
    next.forecast_shipments_delta = new Array(n)
    for (let i = 0; i < n; i++) {
      next.forecast_shipments_delta[i] =
        (next.simulated_shipments_placed_planner_override[i] ?? 0) -
        (next.simulated_shipments_placed_atomic[i] ?? 0)
    }
  }

  // CHO-459 / CHO-463: derive forecast_shipment_revenue per cell from the
  // recomputed planner-override shipments × per-cell price. The viewtransform
  // emits price:MM/dd/yy columns in *displayed-grain* units (USD per case at
  // shipping grain; USD per consumer unit at vividly grain; USD per stick at
  // stick grain). The FE just multiplies — no grain math needed here.
  // Cells with a null price keep their existing baseline FSR value (don't
  // fabricate $0 from missing data; protects against transitional parquets
  // that ship without the price column for a release).
  if (unitPriceShipping) {
    const baseline = next.forecast_shipment_revenue
    const fsr = baseline ? [...baseline] : new Array(n).fill(0)
    let touched = false
    for (let i = 0; i < n; i++) {
      const price = unitPriceShipping[i]
      if (price != null) {
        const qty = next.simulated_shipments_placed_planner_override[i] ?? 0
        fsr[i] = qty * price
        touched = true
      }
    }
    if (touched) next.forecast_shipment_revenue = fsr
  }

  return next
}

export interface ParityRunCell {
  weekIdx: number
  value: number
}

/**
 * After raising override at `editedWeek` above its engine baseline, propose a
 * forward run of cell adjustments (zeros, plus a possibly-partial final cell)
 * that brings cumulative override sum back to exact parity with cumulative
 * baseline.
 *
 * `originalOverride` is the saved (pre-session) override snapshot: cells whose
 * current value differs from it are in-session edits and are left alone so the
 * suggestion doesn't clobber what the planner just typed. Saved overrides from
 * prior sessions ARE eligible candidates — the loop reduces them by their
 * current value (not baseline), so surplus accounting stays correct when a
 * cell's override sits below baseline.
 *
 * Returns [] when surplus <= 0 or no eligible reducible cells exist.
 */
export function suggestParityRun(
  baseline: number[],
  override: number[],
  originalOverride: number[],
  editedWeek: number,
): ParityRunCell[] {
  // Cumulative surplus through the edited week — captures the net override
  // bias against baseline, so prior compensating edits (e.g. an earlier zero
  // run) are properly netted out before suggesting more adjustments.
  let surplus = 0
  for (let i = 0; i <= editedWeek; i++) {
    surplus += (override[i] ?? 0) - (baseline[i] ?? 0)
  }
  if (surplus <= 0) return []

  const out: ParityRunCell[] = []
  for (let i = editedWeek + 1; i < baseline.length; i++) {
    const cur = override[i] ?? 0
    // Nothing to reduce — already at zero (or negative); skip.
    if (cur <= 0) continue
    // Leave in-session edits alone (cells the planner just typed).
    if (!sameRounded(cur, originalOverride[i] ?? 0)) continue
    if (cur <= surplus) {
      out.push({ weekIdx: i, value: 0 })
      surplus -= cur
      if (surplus < 1) break
    } else {
      // Final partial cell to land cumulative override sum exactly on parity.
      out.push({ weekIdx: i, value: cur - surplus })
      surplus = 0
      break
    }
  }
  return out
}

/** Aggregate one first-receiver group into a summary row. Pulled out of
 *  `aggregateFirstReceivers` so callers with cached/dirty-aware loops can
 *  recompute a single FR without rewalking every SKU.
 *
 *  `getMeasures` resolves the *effective* measures for each SKU — pass
 *  `(row) => editedMeasures.get(row.id) ?? row.measures` to fold in-session
 *  edits into the aggregate, or omit for the saved-state aggregation. */
export function aggregateOneFirstReceiver(
  firstReceiver: string,
  skus: SkuRow[],
  periodCount: number,
  aggregateBy: string,
  getMeasures: (row: SkuRow) => Record<string, (number | null)[]> = (row) => row.measures,
): FirstReceiverSummary {
  const isDOS = aggregateBy === "starting_days_of_supply" || aggregateBy === "ending_days_of_supply"
  // Track per-period sums and "did any SKU contribute a non-null value?"
  // flags. If no SKU contributed for a period, the aggregate stays null and
  // renders as "—" rather than collapsing to a misleading 0. Today this is
  // load-bearing for pure-fallback FRs on a suppressed aggregateBy
  // (starting_inventory / actual_sales / ending_inventory /
  // ending_days_of_supply) where the engine emits null on every SKU.
  const totalEnding = new Array(periodCount).fill(0)
  const hasEnding = new Array(periodCount).fill(false)
  const totalSales = new Array(periodCount).fill(0)
  const totalMeasure = new Array(periodCount).fill(0)
  const hasMeasure = new Array(periodCount).fill(false)

  // Sales-weighted target WOS: when inventory_i = target_i * weekly_sales_i
  // for every SKU, portfolio DOS = sum(target_i * sales_i) / sum(sales_i).
  // This is the threshold at which the aggregate row shows "healthy" against
  // the same rules applied per-SKU in the supply walk rows below.
  let weightedTargetNum = 0
  let weightedTargetDen = 0
  for (const sku of skus) {
    const eff = getMeasures(sku)
    const ending = eff.ending_inventory ?? []
    const actual = eff.actual_sales ?? []
    const forecast = eff.forecast_sales ?? []
    const isAct = eff.is_actuals ?? []
    const measure = eff[aggregateBy] ?? []
    let skuTotalSales = 0
    for (let i = 0; i < periodCount; i++) {
      if (ending[i] != null) {
        totalEnding[i] += ending[i] as number
        hasEnding[i] = true
      }
      // Sales: fall back to forecast on actuals weeks. Both can be null on
      // fallback rows (engine suppresses actual_sales); coalesce to 0 for
      // the DoS math but don't gate the aggregate's null-ness on it — that
      // signal comes from `hasEnding`/`hasMeasure` instead.
      const s = isAct[i] ? (actual[i] ?? forecast[i] ?? 0) : (forecast[i] ?? 0)
      totalSales[i] += s
      if (measure[i] != null) {
        totalMeasure[i] += measure[i] as number
        hasMeasure[i] = true
      }
      skuTotalSales += Math.abs(s)
    }
    if (sku.targetWOS != null && skuTotalSales > 0) {
      weightedTargetNum += sku.targetWOS * skuTotalSales
      weightedTargetDen += skuTotalSales
    }
  }
  const targetWOS: number | null =
    weightedTargetDen > 0 ? weightedTargetNum / weightedTargetDen : null

  // For DoS: compute over summed ending/sales, then null out periods where
  // no SKU contributed a non-null ending. For raw measures: emit null when
  // no SKU contributed.
  let aggValues: (number | null)[]
  if (isDOS) {
    const dos = computeDaysOfSupply(totalEnding, totalSales)
    aggValues = dos.map((v, i) => (hasEnding[i] ? v : null))
  } else {
    aggValues = totalMeasure.map((v, i) => (hasMeasure[i] ? v : null))
  }

  // VT output denormalises status/status_updated_at onto every row in the
  // first-receiver group — take from the first sku.
  const first = skus[0]
  return {
    firstReceiver,
    skuCount: skus.length,
    aggregateValues: aggValues,
    targetWOS,
    status: first.status,
    statusUpdatedAt: first.statusUpdatedAt,
  }
}

/** Aggregate SKU rows into first-receiver-level summaries. */
export function aggregateFirstReceivers(
  rows: SkuRow[],
  periodCount: number,
  aggregateBy: string = "starting_days_of_supply",
): FirstReceiverSummary[] {
  const grouped = new Map<string, SkuRow[]>()
  for (const r of rows) {
    if (!grouped.has(r.firstReceiver)) grouped.set(r.firstReceiver, [])
    grouped.get(r.firstReceiver)!.push(r)
  }
  const summaries: FirstReceiverSummary[] = []
  for (const [firstReceiver, skus] of grouped) {
    summaries.push(aggregateOneFirstReceiver(firstReceiver, skus, periodCount, aggregateBy))
  }
  return summaries.sort((a, b) => a.firstReceiver.localeCompare(b.firstReceiver))
}
